import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Search, Phone, MapPin, UserPlus, Trash2, Upload, Download, X, Loader2, AlertTriangle, Eye, EyeOff, UserCheck, Undo2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import AdminLayout from '@/components/AdminLayout';
import { adminAPI } from '@/lib/api';
import { formatDate } from '@/lib/dateUtils';
import { useBranchFilter } from '@/components/BranchFilterContext';

export default function AdminEmployees() {
  const navigate = useNavigate();
  const { branch } = useBranchFilter();
  const [employees, setEmployees] = useState([]);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [showCSV, setShowCSV] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [csvContent, setCsvContent] = useState('');
  const [csvLoading, setCsvLoading] = useState(false);
  const [csvResult, setCsvResult] = useState(null);
  const fileRef = useRef(null);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const PAGE_SIZE = 50;

  // Add employee form
  const [addForm, setAddForm] = useState({ name: '', phone: '', password: '', department: '', designation: '', reporting_manager: '', branch: '', joining_date: '', employee_number: '', holiday_category: 'General' });
  const [addLoading, setAddLoading] = useState(false);
  const [holidayCategories, setHolidayCategories] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [branches, setBranches] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [rms, setRMs] = useState([]);

  const fetchEmployees = async (status, pg = page) => {
    setLoading(true);
    try {
      const [res, zonesRes, catRes] = await Promise.all([
        adminAPI.getEmployees(status === 'all' ? null : status, pg, PAGE_SIZE, branch),
        adminAPI.getZones(),
        adminAPI.getHolidayCategories(),
      ]);
      setEmployees(res.data.employees || res.data);
      setTotalCount(res.data.total || 0);
      setZones(zonesRes.data || []);
      setHolidayCategories(catRes.data || []);
    } catch {
      toast.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
    // Non-critical dropdown data — don't block main list
    try {
      const [branchRes, desigRes, rmRes] = await Promise.all([
        adminAPI.getBranches(),
        adminAPI.getDesignations(),
        adminAPI.getRMs(),
      ]);
      setBranches(branchRes.data || []);
      setDesignations(desigRes.data || []);
      setRMs(rmRes.data || []);
    } catch { /* dropdown data optional */ }
  };

  useEffect(() => { setPage(1); fetchEmployees(tab, 1); }, [tab, branch]);
  useEffect(() => { fetchEmployees(tab, page); }, [page]);

  const filtered = employees.filter(e =>
    e.name?.toLowerCase().includes(search.toLowerCase()) ||
    e.phone?.includes(search)
  );

  const handleAddEmployee = async () => {
    if (!addForm.name || !addForm.phone || !addForm.password) {
      toast.error('Name, phone and password are required');
      return;
    }
    setAddLoading(true);
    try {
      await adminAPI.createEmployee(addForm);
      toast.success('Employee added');
      setShowAdd(false);
      setAddForm({ name: '', phone: '', password: '', department: '', designation: '', reporting_manager: '', branch: '', joining_date: '', employee_number: '', holiday_category: 'General' });
      fetchEmployees(tab);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add');
    } finally {
      setAddLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setDeleting(true);
    try {
      await adminAPI.deleteEmployee(id);
      toast.success('Employee moved to Deleted (can be restored)');
      setDeleteId(null);
      fetchEmployees(tab);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  const handleRestore = async (id) => {
    try {
      await adminAPI.restoreEmployee(id);
      toast.success('Employee restored');
      fetchEmployees(tab);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to restore');
    }
  };

  const handleCSVFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setCsvContent(reader.result);
    reader.readAsText(file);
  };

  const handleCSVImport = async () => {
    if (!csvContent) { toast.error('Upload a CSV file first'); return; }
    setCsvLoading(true);
    try {
      const res = await adminAPI.importEmployeesCSV(csvContent);
      setCsvResult(res.data);
      toast.success(`${res.data.created} employees imported`);
      fetchEmployees(tab);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setCsvLoading(false);
    }
  };

  const employeeCSVTemplate = `Name,Phone,Password,Department,Designation,Reporting Manager,Branch,Joining Date,Gender,DOB,Address,Employee Number,PF Number,Emergency Contact Name,Emergency Contact Number,Sanctioned Leaves,Holiday Category
John Doe,9876543210,pass1234,Sales,Executive,Jane Smith,Mumbai HQ,2024-01-15,male,1990-05-20,123 Main St Mumbai,EMP001,PF12345,Mary Doe,9876543211,18,General
`;

  const downloadCSVTemplate = () => {
    try {
      const blob = new Blob([employeeCSVTemplate], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'employee_template.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      // Fallback: open as data URI
      const encoded = encodeURIComponent(employeeCSVTemplate);
      window.open('data:text/csv;charset=utf-8,' + encoded);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 data-testid="admin-employees-title" className="text-2xl font-extrabold text-[#1a2529]">Employees</h1>
            <p className="text-sm text-[#1a2529]/50">{totalCount} total</p>
          </div>
          <div className="flex gap-2">
            <Button data-testid="add-employee-btn" onClick={() => setShowAdd(true)} size="sm" className="bg-[#026536] hover:bg-[#026536]/90 gap-1.5 font-semibold">
              <UserPlus size={14} /> Add
            </Button>
            <Button data-testid="csv-import-btn" onClick={() => setShowCSV(true)} size="sm" variant="outline" className="gap-1.5 font-semibold">
              <Upload size={14} /> CSV Import
            </Button>
          </div>
        </div>

        {/* Add Employee Modal */}
        {showAdd && (
          <div className="bg-white rounded-xl p-5 border border-border/40 shadow-sm animate-slide-up">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-[#1a2529]">Add New Employee</h3>
              <button onClick={() => setShowAdd(false)}><X size={18} className="text-[#1a2529]/40" /></button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold text-[#1a2529]/70">Name *</Label>
                <Input data-testid="add-name" value={addForm.name} onChange={e => setAddForm({ ...addForm, name: e.target.value })} placeholder="Full name" className="h-10 mt-1" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#1a2529]/70">Phone *</Label>
                <Input data-testid="add-phone" value={addForm.phone} onChange={e => setAddForm({ ...addForm, phone: e.target.value.replace(/\D/g, '') })} placeholder="10-digit number" maxLength={10} className="h-10 mt-1" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#1a2529]/70">Password *</Label>
                <div className="relative mt-1">
                  <Input data-testid="add-password" type={showPassword ? 'text' : 'password'} value={addForm.password} onChange={e => setAddForm({ ...addForm, password: e.target.value })} placeholder="Login password" className="h-10 pr-10" />
                  <button type="button" data-testid="toggle-password-visibility" onClick={() => setShowPassword(!showPassword)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#1a2529]/30 hover:text-[#1a2529]/60 transition-colors">
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#1a2529]/70">Department</Label>
                <Input value={addForm.department} onChange={e => setAddForm({ ...addForm, department: e.target.value })} className="h-10 mt-1" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#1a2529]/70">Designation</Label>
                <select data-testid="add-designation" value={addForm.designation} onChange={e => setAddForm({ ...addForm, designation: e.target.value })}
                  className="w-full h-10 mt-1 border rounded-md px-3 text-sm bg-white">
                  <option value="">Select designation</option>
                  {designations.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#1a2529]/70">Reporting Manager</Label>
                <select data-testid="add-reporting-manager" value={addForm.reporting_manager} onChange={e => setAddForm({ ...addForm, reporting_manager: e.target.value })}
                  className="w-full h-10 mt-1 border rounded-md px-3 text-sm bg-white">
                  <option value="">Select RM</option>
                  {rms.map(r => <option key={r.id} value={r.name}>{r.name}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#1a2529]/70">Branch</Label>
                <select data-testid="add-branch" value={addForm.branch} onChange={e => setAddForm({ ...addForm, branch: e.target.value })}
                  className="w-full h-10 mt-1 border rounded-md px-3 text-sm bg-white">
                  <option value="">Select branch</option>
                  {branches.map(b => <option key={b.id} value={b.name}>{b.name}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#1a2529]/70">Joining Date</Label>
                <Input type="date" value={addForm.joining_date} onChange={e => setAddForm({ ...addForm, joining_date: e.target.value })} className="h-10 mt-1" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#1a2529]/70">Employee #</Label>
                <Input value={addForm.employee_number} onChange={e => setAddForm({ ...addForm, employee_number: e.target.value })} className="h-10 mt-1" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#1a2529]/70">Holiday Category</Label>
                <select data-testid="add-holiday-category-select" value={addForm.holiday_category} onChange={e => setAddForm({ ...addForm, holiday_category: e.target.value })}
                  className="w-full h-10 mt-1 border rounded-md px-3 text-sm bg-white">
                  {holidayCategories.map(c => <option key={c.id || c} value={c.name || c}>{c.name || c}</option>)}
                </select>
              </div>
            </div>
            <Button data-testid="confirm-add-employee" onClick={handleAddEmployee} disabled={addLoading} className="mt-4 bg-[#026536] hover:bg-[#026536]/90 font-semibold gap-2">
              {addLoading ? <Loader2 className="animate-spin" size={14} /> : <UserPlus size={14} />} Add Employee
            </Button>
          </div>
        )}

        {/* CSV Import Modal */}
        {showCSV && (
          <div className="bg-white rounded-xl p-5 border border-border/40 shadow-sm animate-slide-up">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-[#1a2529]">Import Employees via CSV</h3>
              <button onClick={() => { setShowCSV(false); setCsvContent(''); setCsvResult(null); }}><X size={18} className="text-[#1a2529]/40" /></button>
            </div>
            <button data-testid="download-template" onClick={downloadCSVTemplate} className="text-xs text-[#026536] font-semibold hover:underline mb-3 flex items-center gap-1">
              <Download size={12} /> Download CSV Template
            </button>
            <div className="border-2 border-dashed border-border/40 rounded-xl p-4 text-center">
              <input data-testid="csv-file-input" type="file" accept=".csv" onChange={handleCSVFile} ref={fileRef} className="hidden" />
              <button onClick={() => fileRef.current?.click()} className="text-sm text-[#1a2529]/50 font-medium">
                {csvContent ? `File loaded (${csvContent.split('\n').length - 1} rows)` : 'Click to select CSV file'}
              </button>
            </div>
            {csvResult && (
              <div data-testid="csv-result" className="mt-3 bg-[#f4f4f5] rounded-xl p-3 text-xs space-y-1">
                <p className="font-semibold text-[#026536]">Created: {csvResult.created}</p>
                {csvResult.skipped > 0 && <p className="text-[#dea439]">Skipped: {csvResult.skipped}</p>}
                {csvResult.default_password_used && <p className="text-[#1a2529]/60 font-medium">No "Password" column found — phone number was set as the default password for imported employees.</p>}
                {csvResult.errors?.map((e, i) => <p key={i} className="text-red-500">{e}</p>)}
              </div>
            )}
            <Button data-testid="import-csv-btn" onClick={handleCSVImport} disabled={csvLoading || !csvContent} className="mt-3 bg-[#026536] hover:bg-[#026536]/90 font-semibold gap-2">
              {csvLoading ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />} Import
            </Button>
          </div>
        )}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-white h-10">
            <TabsTrigger value="all" className="font-medium">All</TabsTrigger>
            <TabsTrigger value="approved" className="font-medium data-[state=active]:text-[#026536]">Approved</TabsTrigger>
            <TabsTrigger value="pending" className="font-medium data-[state=active]:text-[#dea439]">Pending</TabsTrigger>
            <TabsTrigger value="deleted" className="font-medium data-[state=active]:text-red-500">Deleted</TabsTrigger>
          </TabsList>
          {totalCount > 0 && (
            <span data-testid="employee-total-count" className="text-xs text-[#1a2529]/50 ml-2 font-medium">{totalCount} total</span>
          )}
        </Tabs>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#1a2529]/30" size={16} />
          <Input data-testid="employee-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or phone..." className="pl-10 h-10 bg-white" />
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="h-8 w-8 border-2 border-[#026536] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-xl p-8 text-center shadow-sm">
            <Users className="mx-auto text-[#1a2529]/15 mb-3" size={40} />
            <p className="font-medium text-[#1a2529]/50">No employees found</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {filtered.map((emp, idx) => {
              const statusColor = emp.status === 'approved' ? 'bg-[#026536]' : emp.status === 'pending' ? 'bg-[#dea439]' : emp.status === 'deleted' ? 'bg-gray-400' : 'bg-red-500';
              const zoneIds = emp.zone_ids || (emp.zone_id ? [emp.zone_id] : []);
              const zoneNames = zoneIds.map(zid => zones.find(z => z.id === zid)?.name).filter(Boolean);
              return (
                <div key={emp.id} data-testid={`employee-card-${idx}`} className="bg-white rounded-xl p-3 shadow-sm border border-border/30 hover:shadow-md hover:border-[#026536]/20 transition-all group cursor-pointer relative animate-slide-up" style={{ animationDelay: `${Math.min(idx, 15) * 20}ms` }} onClick={() => navigate(`/admin/employees/${emp.id}`)}>
                  {/* Status dot */}
                  <div className={`absolute top-2.5 right-2.5 h-2 w-2 rounded-full ${statusColor}`} title={emp.status} />

                  {/* Action buttons - show on hover */}
                  <div className="absolute top-1.5 right-6 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity" onClick={e => e.stopPropagation()}>
                    {emp.status === 'deleted' ? (
                      <button data-testid={`restore-emp-${idx}`} onClick={() => handleRestore(emp.id)} className="h-6 w-6 rounded flex items-center justify-center hover:bg-green-50 text-[#1a2529]/30 hover:text-[#026536] transition-colors" title="Restore">
                        <Undo2 size={11} />
                      </button>
                    ) : (
                      <button data-testid={`delete-emp-${idx}`} onClick={() => setDeleteId(emp.id)} className="h-6 w-6 rounded flex items-center justify-center hover:bg-red-50 text-[#1a2529]/30 hover:text-red-500 transition-colors" title="Delete">
                        <Trash2 size={11} />
                      </button>
                    )}
                  </div>

                  {/* Avatar */}
                  <div className="flex flex-col items-center text-center mb-2">
                    {emp.profile_photo ? (
                      <img src={emp.profile_photo} alt={emp.name} className="h-11 w-11 rounded-full object-cover mb-1.5 ring-2 ring-[#026536]/10" />
                    ) : (
                      <div className="h-11 w-11 rounded-full bg-[#026536]/8 flex items-center justify-center text-sm font-bold text-[#026536] mb-1.5 ring-2 ring-[#026536]/10">
                        {emp.name?.[0]?.toUpperCase()}
                      </div>
                    )}
                    <p className="font-semibold text-[#1a2529] text-xs leading-tight line-clamp-1 w-full">{emp.name}</p>
                    <span className="text-[10px] text-[#1a2529]/40 mt-0.5 flex items-center gap-1">
                      <Phone size={9} /> {emp.phone}
                    </span>
                  </div>

                  {/* Meta info */}
                  <div className="space-y-1 border-t border-border/20 pt-1.5">
                    {emp.designation && (
                      <p className="text-[10px] text-[#1a2529]/50 truncate">{emp.designation}</p>
                    )}
                    {emp.branch && (
                      <p className="text-[10px] text-[#1a2529]/40 truncate flex items-center gap-0.5"><MapPin size={8} />{emp.branch}</p>
                    )}
                    {emp.reporting_manager && (
                      <p className="text-[10px] text-purple-500 truncate flex items-center gap-0.5"><UserCheck size={8} />{emp.reporting_manager}</p>
                    )}
                    {zoneNames.length > 0 && (
                      <p className="text-[10px] text-[#026536] truncate">{zoneNames.length <= 2 ? zoneNames.join(', ') : `${zoneNames[0]} +${zoneNames.length - 1}`}</p>
                    )}
                    {emp.deleted_at && (
                      <p className="text-[10px] text-red-400 truncate">Deleted {formatDate(emp.deleted_at.split('T')[0])}</p>
                    )}
                  </div>

                  {/* Pending actions */}
                  {emp.status === 'pending' && (
                    <div className="flex gap-1.5 mt-2" onClick={e => e.stopPropagation()}>
                      <button data-testid={`approve-emp-${idx}`} onClick={() => { adminAPI.updateEmployeeStatus(emp.id, 'approved').then(() => { toast.success('Approved'); fetchEmployees(tab); }); }} className="flex-1 h-6 rounded-md bg-[#026536] hover:bg-[#026536]/90 text-white text-[10px] font-semibold transition-colors">Approve</button>
                      <button data-testid={`reject-emp-${idx}`} onClick={() => { adminAPI.updateEmployeeStatus(emp.id, 'rejected').then(() => { toast.success('Rejected'); fetchEmployees(tab); }); }} className="flex-1 h-6 rounded-md border border-red-200 text-red-500 hover:bg-red-50 text-[10px] font-semibold transition-colors">Reject</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalCount > PAGE_SIZE && (
          <div data-testid="pagination-controls" className="flex items-center justify-between bg-white rounded-xl p-3 shadow-sm border border-border/30">
            <span className="text-xs text-[#1a2529]/50">
              Showing {Math.min((page - 1) * PAGE_SIZE + 1, totalCount)}–{Math.min(page * PAGE_SIZE, totalCount)} of {totalCount}
            </span>
            <div className="flex items-center gap-2">
              <button data-testid="prev-page-btn" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} className="h-8 w-8 rounded-lg flex items-center justify-center border border-border/50 disabled:opacity-30 hover:bg-[#026536]/5 transition-colors">
                <ChevronLeft size={14} />
              </button>
              <span className="text-xs font-medium text-[#1a2529]/70">Page {page} of {Math.ceil(totalCount / PAGE_SIZE)}</span>
              <button data-testid="next-page-btn" onClick={() => setPage(p => Math.min(Math.ceil(totalCount / PAGE_SIZE), p + 1))} disabled={page >= Math.ceil(totalCount / PAGE_SIZE)} className="h-8 w-8 rounded-lg flex items-center justify-center border border-border/50 disabled:opacity-30 hover:bg-[#026536]/5 transition-colors">
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation */}
      {deleteId && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full animate-slide-up">
            <AlertTriangle className="text-red-500 mb-3" size={28} />
            <h3 className="font-bold text-[#1a2529] text-lg">Delete Employee?</h3>
            <p className="text-sm text-[#1a2529]/50 mt-1">This will move the employee to the Deleted tab. You can restore them later. Their data (attendance, leaves, sanctions) will be preserved.</p>
            <div className="flex gap-2 mt-5">
              <Button data-testid="confirm-delete-btn" onClick={() => handleDelete(deleteId)} disabled={deleting} className="flex-1 bg-red-500 hover:bg-red-600 text-white font-semibold">
                {deleting ? <Loader2 className="animate-spin mr-2" size={14} /> : <Trash2 size={14} className="mr-2" />} Delete
              </Button>
              <Button onClick={() => setDeleteId(null)} variant="outline" className="flex-1 font-semibold">Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
